
import React, { useState, useRef, useEffect } from 'react';
import { Language } from '../types';
import { LANGUAGE_NAMES } from '../translations';
import { Globe, Check, ChevronDown } from 'lucide-react';

interface LanguageDropdownProps {
  currentLanguage: Language;
  onChange: (lang: Language) => void;
}

export const LanguageDropdown: React.FC<LanguageDropdownProps> = ({ currentLanguage, onChange }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-slate-800 text-slate-300 transition-colors"
      >
        <Globe size={20} />
        <span className="hidden md:inline text-sm font-medium">{LANGUAGE_NAMES[currentLanguage]}</span>
        <ChevronDown size={14} className={`transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <div className="absolute right-0 top-12 w-48 bg-slate-800 border border-slate-700 rounded-xl shadow-xl max-h-96 overflow-y-auto z-50 animate-in fade-in slide-in-from-top-2">
          <div className="p-1">
            {Object.entries(LANGUAGE_NAMES).map(([code, name]) => (
              <button
                key={code}
                onClick={() => {
                  onChange(code as Language);
                  setIsOpen(false);
                }}
                className={`w-full flex items-center justify-between px-3 py-2 text-sm rounded-lg transition-colors ${
                  currentLanguage === code
                    ? 'bg-indigo-600 text-white'
                    : 'text-slate-300 hover:bg-slate-700'
                }`}
              >
                <span>{name}</span>
                {currentLanguage === code && <Check size={14} />}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
